import type { TFunction } from 'i18next';
import type { IObservableArray } from 'mobx';

import type { Targeting } from '@feathr/blackbox';

import { getGeoTargetings } from '../Targetings.utils';

/**
 * Enum representing the available geofilter modes.
 */
export enum EGeoFilterMode {
  NONE = 'none',
  SAVED_SET = 'saved_set',
  CUSTOM_SET = 'custom_set',
}

/**
 * Option type for geofilter mode radio selection.
 */
export interface IGeoFilterModeOption {
  id: EGeoFilterMode;
  name: string;
}

/**
 * Determines the current geofilter mode based on existing targetings.
 *
 * @param targetings - Observable array of targetings to check
 * @returns The determined geofilter mode
 *
 * @example
 * const mode = determineGeoFilterMode(targetings);
 */
export function determineGeoFilterMode(targetings: IObservableArray<Targeting>): EGeoFilterMode {
  const geoTargetings = getGeoTargetings(targetings);

  if (geoTargetings.length === 0) {
    return EGeoFilterMode.NONE;
  }

  const hasGeoSet = geoTargetings.some((targeting) => targeting.get('kind') === 'geo_set');

  return hasGeoSet ? EGeoFilterMode.SAVED_SET : EGeoFilterMode.CUSTOM_SET;
}

/**
 * Returns the geofilter mode options for the radio selection.
 *
 * @param t - Translation function
 * @returns Array of geofilter mode options
 *
 * @example
 * const options = getGeoFilterModeOptions(t);
 */
export function getGeoFilterModeOptions(t: TFunction): IGeoFilterModeOption[] {
  return [
    {
      id: EGeoFilterMode.NONE,
      name: t("Don't use geofilters"),
    },
    {
      id: EGeoFilterMode.SAVED_SET,
      name: t('Use a saved set'),
    },
    {
      id: EGeoFilterMode.CUSTOM_SET,
      name: t('Use custom geofilters'),
    },
  ];
}

/**
 * Returns validation errors for saving a custom set.
 *
 * @param filteredTargetingsCount - Number of filtered targetings
 * @param hasInvalidTargetings - Whether any targetings are invalid
 * @param t - Translation function
 * @returns Array of error messages
 *
 * @example
 * const errors = getSaveCustomSetErrors(targetings.length, hasInvalid, t);
 */
export function getSaveCustomSetErrors(
  filteredTargetingsCount: number,
  hasInvalidTargetings: boolean,
  t: TFunction,
): string[] {
  const errors: string[] = [];

  if (filteredTargetingsCount === 0) {
    errors.push(t('Add at least one filter to save a set'));
  }
  if (hasInvalidTargetings) {
    errors.push(t('Complete all filters before saving'));
  }

  return errors;
}
